import {
  AuthProvider,
  NetworkProvider as ToolkitNetworkProvider,
} from '@locus-taxy/encore-ui-network';
import React from 'react';
import { useAuth0 } from '../../auth';
import { registerEndpoints } from './endpoints';
import { getApiBaseUrl } from '../../config';
import { useAuthIntegration } from './auth';
import { isRunningInWebView } from './nativeBridge';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Box } from '@locus-taxy/encore-mui';
import LoginPage from '../../components/LoginPage';
import { useUser } from '../../hooks/useUser';
import InterceptorInstaller from './InterceptorInstaller';
import { useClientId } from '../../hooks/useClientId';

interface NetworkProviderProps {
  children: React.ReactNode;
  baseUrl?: string;
}

const LoadingSpinnerComponent: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <LoadingSpinner showRandomMessage={false} />
  </Box>
);

const shouldShowLoadingSpinner = (
  inWebView: boolean,
  isNetworkAuthReady: boolean,
  isAuthLoading: boolean,
  isAuthenticated: boolean
): boolean => {
  if (inWebView) {
    return !isNetworkAuthReady;
  }
  return isAuthLoading || (isAuthenticated && !isNetworkAuthReady);
};

const shouldWaitForClientId = (
  inWebView: boolean,
  isAuthenticated: boolean,
  clientId?: string
): boolean => {
  return !clientId && !inWebView && isAuthenticated;
};

export const NetworkProvider: React.FC<NetworkProviderProps> = ({
  children,
  baseUrl: baseUrlProp,
}) => {
  const { isNetworkAuthReady } = useAuthIntegration();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const inWebView = isRunningInWebView();
  const { user } = useUser();
  const clientId = useClientId();
  // Construct dynamic base URL using clientId
  // Use prop if provided (for backward compatibility), otherwise construct dynamically
  const baseUrl = React.useMemo(() => {
    if (baseUrlProp) {
      return baseUrlProp;
    }
    try {
      // getApiBaseUrl handles the case where clientId is missing by providing a fallback
      return getApiBaseUrl(clientId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[NetworkProvider] Failed to construct API base URL:',
        error instanceof Error ? error.message : error
      );
      // Return null as safe fallback - component will handle this case
      return null;
    }
  }, [baseUrlProp, clientId]);

  const onBehalfOf = React.useMemo(() => {
    const userId = user?.userId;
    if (!userId) {
      return 'anonymous';
    }
    const organizationId = user?.homeTenant;
    if (!organizationId) {
      return 'anonymous';
    }
    return `${organizationId}/${userId}`;
  }, [user]);

  // Register endpoints synchronously
  registerEndpoints();

  // Don't render until auth is ready
  if (
    shouldShowLoadingSpinner(
      inWebView,
      isNetworkAuthReady,
      isAuthLoading,
      isAuthenticated
    )
  ) {
    return <LoadingSpinnerComponent />;
  }

  // Don't render until we have a baseUrl (clientId is available)
  // Only show spinner if we ARE authenticated and missing a real clientId,
  // because if NOT authenticated, we want to show the LoginPage
  if (shouldWaitForClientId(inWebView, isAuthenticated, clientId)) {
    return <LoadingSpinnerComponent />;
  }

  // Handle case where baseUrl construction failed (missing apiBaseDomain config)
  if (!baseUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            color: 'error.main',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Box sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
            Configuration Error
          </Box>
          <Box>
            Missing API base URL configuration. Please check your environment
            configuration file.
          </Box>
        </Box>
      </Box>
    );
  }

  const shouldShowInterceptor = !inWebView && baseUrl;
  const shouldShowLoginPage = !inWebView && !isAuthenticated;

  return (
    <ToolkitNetworkProvider>
      <AuthProvider baseUrl={baseUrl}>
        {shouldShowInterceptor && (
          <InterceptorInstaller
            baseUrl={baseUrl}
            getAccessTokenSilently={getAccessTokenSilently}
            onBehalfOf={onBehalfOf}
          />
        )}
        {shouldShowLoginPage ? <LoginPage /> : children}
      </AuthProvider>
    </ToolkitNetworkProvider>
  );
};
